import React, { useContext, useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import Cookies from 'js-cookie';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SurveyTheme } from '@sm/webassets';
import { t } from '@sm/intl';
import { USER_EVENTS } from '@sm/metrics';

import { store } from '~app/store';

import { GetCurrentSurveySessionQuery, GetCurrentSurveySessionQueryVariables } from '~lib/generatedGqlTypes';
import { BiEventsDataType, StaticContextType } from './types';

import Survey from './Survey';
import setSurveyTakingDocumentTitle from './helper/surveyTakingWindowTitle';
import generateEventData from './helper/generateEventDataForLogs';
import SEOBanner from '~app/components/SEOBanner';
import GoToURL from '~app/components/Survey/GoToURL';
import { FourOhFourErrorPage } from '~app/components/Errors';

import getCurrentSurveySession from './SurveyTaking.graphql';
import getSpageGoogleFontsLink from '~app/helpers/spageGoogleFonts';

import { ErrorType, SurveyErrorsProvider, useSurveyErrors } from './errors';
import SurveyProvider from './SurveyContext';
import CurrentPageProvider from './CurrentPageContext';

const COPY = {
  META_DESCRIPTION: {
    id: 'SurveyTaking.metaDescription',
    defaultMessage:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
  SEO_BANNER_CLOSE_BUTTON_ARIA_TEXT: {
    id: 'SurveyTaking.seoBannerCloseButtonAriaText',
    defaultMessage: 'close SEO banner',
  },
  SEO_BANNER_BODY: {
    id: 'SurveyTaking.seoBannerBody',
    defaultMessage: "Is this the survey you're looking for? Try creating your own with the world's leading platform.",
  },
  SEO_BANNER_SIGNUP_BUTTON: {
    id: 'SurveyTaking.seoBannerSignupButton',
    defaultMessage: 'Sign up free',
  },
  SEO_BANNER_LEARN_MORE: {
    id: 'SurveyTaking.seoBannerLearnMoreButton',
    defaultMessage: 'Learn more',
  },
};

const SurveyTaking: React.FC = () => {
  const {
    pageOptions: { isRequestFromSearchEngine, isWhiteLabel, subdomains, collectorKey, clientToken },
    environment,
    user,
  } = useContext<StaticContextType>(StaticContext);

  const surveyErrorsApi = useSurveyErrors();

  const {
    loading: loadingSessionData,
    error: errorSessionData,
    data: currentSurveySessionData,
  } = useQuery<GetCurrentSurveySessionQuery, GetCurrentSurveySessionQueryVariables>(getCurrentSurveySession, {
    variables: {
      collectionKey: collectorKey,
    },
    context: {
      headers: {
        'x-client-token': clientToken,
      },
    },
  });

  useEffect(() => {
    surveyErrorsApi.setQuestionOrder(
      currentSurveySessionData?.surveyTakingSession.respondent.currentPage?.questionsQM?.items.map(q => q.id) || []
    );
  }, [surveyErrorsApi, currentSurveySessionData?.surveyTakingSession.respondent.currentPage?.questionsQM?.items]);

  if (loadingSessionData) {
    // Placeholder - replace with proper component when ready
    return <span>loading...</span>;
  }

  if (!currentSurveySessionData?.surveyTakingSession?.respondent?.survey) {
    return <FourOhFourErrorPage />;
  }

  // Errors should be handled after checking survey session data is present
  if (errorSessionData) {
    throw errorSessionData;
  }

  // seo banner config
  const seoBannerCallToActionButtons = [
    {
      label: t(COPY.SEO_BANNER_SIGNUP_BUTTON),
      uri: 'https://www.surveymonkey.com/user/sign-up/?ut_source=s_page_search_topbar_desktop',
    },
    {
      label: t(COPY.SEO_BANNER_LEARN_MORE),
      uri: 'https://www.surveymonkey.com/mp/take-a-tour/?ut_source=s_page_search_topbar_desktop',
    },
  ];

  const {
    surveyTakingSession: {
      respondent: { survey, collector, currentPage, collectionMethod },
      encryptedSmParam,
    },
  } = currentSurveySessionData;

  const {
    id: surveyId,
    language: { code: langCode },
    title: surveyTitle,
  } = survey;

  surveyErrorsApi.setMeta({
    [ErrorType.COLLECTOR_CLOSED]: { url: `/survey-closed/?sm=${encryptedSmParam}` },
    [ErrorType.REQUEST_IP_DISALLOWED]: { url: `/survey-closed/?sm=${encryptedSmParam}` },
  });

  const commonBiData = generateEventData({ environment, user, collector, survey, currentPage });
  const SURVEY_FIRST_GET: BiEventsDataType = {
    name: USER_EVENTS.PAGE_VIEW,
    data: {
      actionFlow: 'survey_load',
      actionType: 'first_get',
      ...commonBiData,
    },
  };

  const isTitleEnabled = survey?.design?.surveyTitle.enabled ?? true;
  const spageGoogleFontsLink = getSpageGoogleFontsLink(survey?.design?.theme ?? undefined);
  const respondentCollectionMethod = collectionMethod ?? null;

  if (subdomains && subdomains.length === 1 && subdomains[0] !== collector?.weblink?.subdomain) {
    return <FourOhFourErrorPage />;
  }

  // centralize _redirector() as  more use cases added
  if (!collector?.allowMultipleResponses && Cookies.get(`RP_${collector?.id}`)) {
    return <GoToURL url={`/survey-taken/?sm=${encryptedSmParam}`} />;
  }

  return (
    <ReduxProvider store={store}>
      <SurveyErrorsProvider {...surveyErrorsApi}>
        <BasePage
          actionFlow="SurveyTaking"
          pageId="SurveyTaking"
          legacyWeb="respweb"
          // are the following from survey design response?
          includeHeader={false}
          includeFooter={false}
          includeExperiments={false}
          pageMetricsAttributes={SURVEY_FIRST_GET}
        >
          <Helmet htmlAttributes={{ lang: langCode }}>
            <title>{setSurveyTakingDocumentTitle(isTitleEnabled, isWhiteLabel, surveyTitle)}</title>
            <meta name="description" content={t(COPY.META_DESCRIPTION)} />
            <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
            <meta httpEquiv="content-language" content={langCode} />
            {spageGoogleFontsLink && <link rel="stylesheet" href={spageGoogleFontsLink} />}
          </Helmet>
          <SurveyTheme surveyId={surveyId} targetEndPoint="surveyTaking" headers={{ 'x-client-token': clientToken }}>
            <SurveyProvider survey={survey} currentPage={currentPage} collector={collector} clientToken={clientToken}>
              <SEOBanner
                callToActionButtons={seoBannerCallToActionButtons}
                closeButtonAriaText={t(COPY.SEO_BANNER_CLOSE_BUTTON_ARIA_TEXT)}
                requestFromSearchEngine={isRequestFromSearchEngine}
                collectionMethod={respondentCollectionMethod}
                collectorType={collector?.type}
              >
                {t(COPY.SEO_BANNER_BODY)}
              </SEOBanner>
              <CurrentPageProvider>
                <Survey />
              </CurrentPageProvider>
            </SurveyProvider>
          </SurveyTheme>
        </BasePage>
      </SurveyErrorsProvider>
    </ReduxProvider>
  );
};

export default SurveyTaking;
